import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Sparkles,
  Archive,
  ArchiveRestore,
  User,
  UserPlus,
  Search,
  RefreshCw,
  Info,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  PhoneCall,
  Settings as SettingsIcon,
  FileText,
} from 'lucide-react';
import { formatDateOnly } from '../../utils/timezone';

function relativeTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return `${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    const days = Math.round(hrs / 24);
    if (days < 7) return `${days}d`;
    return formatDateOnly(iso);
  } catch {
    return '';
  }
}

export default function WhatsAppInbox() {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [threads, setThreads] = useState([]);
  const [leads, setLeads] = useState([]);
  const [activeTab, setActiveTab] = useState('active');    // active | archived | leads
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);

  const loadConfig = async () => {
    try {
      const { data } = await api.get('/whatsapp/config');
      setConfig(data);
    } catch (e) {
      // Silently ignore - Alert will show "unknown"
    }
  };

  const loadThreads = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = activeTab === 'archived' ? 'archived' : 'active';
      const { data } = await api.get('/whatsapp/threads', {
        params: { status: statusParam, limit: 200 },
      });
      setThreads(data.threads || []);
    } catch (e) {
      toast.error('Failed to load threads');
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/whatsapp/leads', { params: { limit: 200 } });
      setLeads(data.leads || []);
    } catch (e) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = async (threadId) => {
    setLoadingMessages(true);
    try {
      const { data } = await api.get(`/whatsapp/threads/${threadId}/messages`);
      setMessages(data.messages || []);
      // Mark as read
      await api.post(`/whatsapp/threads/${threadId}/read`).catch(() => {});
      setThreads((prev) =>
        prev.map((t) => (t.id === threadId ? { ...t, unread_count: 0 } : t))
      );
    } catch (e) {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  useEffect(() => {
    if (activeTab === 'leads') {
      loadLeads();
    } else {
      loadThreads();
    }
  }, [activeTab, loadThreads, loadLeads]);

  useEffect(() => {
    if (selectedThread) loadMessages(selectedThread.id);
    else setMessages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedThread?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendReply = async () => {
    if (!reply.trim() || !selectedThread) return;
    setSending(true);
    try {
      const { data } = await api.post(`/whatsapp/threads/${selectedThread.id}/send`, {
        body: reply.trim(),
      });
      if (data.success) {
        toast.success('Message sent');
        setReply('');
        loadMessages(selectedThread.id);
        loadThreads();
      } else {
        toast.error(data.error || 'Failed to send');
        // Reload messages so the failed outbound entry (audit trail) is visible
        loadMessages(selectedThread.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const suggestReply = async () => {
    if (!selectedThread || suggesting) return;
    setSuggesting(true);
    try {
      const steer = reply.trim();
      const { data } = await api.post(
        `/whatsapp/threads/${selectedThread.id}/suggest-reply`,
        steer ? { instruction: steer.slice(0, 500) } : {}
      );
      if (data?.success && data?.draft) {
        setReply(data.draft);
        toast.success(steer ? 'Draft refined from your note' : 'Draft ready — review before sending');
      } else {
        toast.error('Could not generate a draft. Try again.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI draft failed');
    } finally {
      setSuggesting(false);
    }
  };

  const toggleArchive = async (thread) => {
    try {
      const action = thread.status === 'archived' ? 'unarchive' : 'archive';
      await api.post(`/whatsapp/threads/${thread.id}/${action}`);
      toast.success(`Thread ${action}d`);
      setSelectedThread(null);
      loadThreads();
    } catch (e) {
      toast.error('Failed to update thread');
    }
  };

  const updateLead = async (lead, status) => {
    try {
      await api.patch(`/whatsapp/leads/${lead.id}`, { status });
      toast.success(`Lead marked ${status}`);
      loadLeads();
    } catch (e) {
      toast.error('Failed to update lead');
    }
  };

  const filteredThreads = threads.filter((t) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (t.contact_name || '').toLowerCase().includes(q) ||
      (t.parent_name || '').toLowerCase().includes(q) ||
      (t.display_phone || t.phone || '').toLowerCase().includes(q) ||
      (t.last_message_body || '').toLowerCase().includes(q)
    );
  });

  const totalUnread = threads.reduce((sum, t) => sum + (t.unread_count || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="whatsapp-inbox-page">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-green-600" />
              WhatsApp Inbox
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Two-way parent messaging via Meta WhatsApp Business Cloud API.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => (activeTab === 'leads' ? loadLeads() : loadThreads())}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/whatsapp/templates')}
              data-testid="templates-btn"
            >
              <FileText className="w-4 h-4 mr-1" />
              Templates
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin/whatsapp/settings')}
              data-testid="settings-btn"
            >
              <SettingsIcon className="w-4 h-4 mr-1" />
              Settings
            </Button>
          </div>
        </div>

        {/* Number-block banner (Meta rejected sends — e.g. 131037 display name pending) */}
        {config?.number_block && (
          <Alert className="border-rose-500 bg-rose-50 dark:bg-rose-950/20" data-testid="number-block-inbox-banner">
            <AlertTriangle className="w-4 h-4 text-rose-600" />
            <AlertTitle className="text-rose-900 dark:text-rose-100 text-sm">
              Outbound sending paused (Meta error {config.number_block.error_code})
            </AlertTitle>
            <AlertDescription className="text-xs text-rose-800 dark:text-rose-200 space-y-1">
              <div>{config.number_block.error_message}</div>
              {config.number_block.error_code === 131037 && (
                <div>Meta needs to approve your number's <strong>display name</strong> before you can send. Fix it in Meta Business Manager → WhatsApp Manager → Phone Numbers → Settings → Business info → Display Name.</div>
              )}
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs mt-1"
                onClick={() => navigate('/admin/whatsapp/settings')}
                data-testid="open-wa-settings-from-block-banner"
              >
                Open Settings →
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Config Status */}
        {config && !config.configured && (
          <Alert variant="default" className="border-amber-400 bg-amber-50 dark:bg-amber-950/20" data-testid="config-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertTitle className="text-amber-800 dark:text-amber-200">WhatsApp not fully configured</AlertTitle>
            <AlertDescription className="text-xs text-amber-700 dark:text-amber-300 space-y-1">
              <div>Incoming messages still work (webhooks), but outbound sending is disabled.</div>
              <div className="flex items-center gap-2 flex-wrap">
                Missing:{' '}
                {!config.phone_number_id_set && <Badge variant="outline" className="mr-1">Phone Number ID</Badge>}
                {!config.has_access_token && <Badge variant="outline" className="mr-1">Access Token</Badge>}
                {!config.has_app_secret && <Badge variant="outline" className="mr-1">App Secret</Badge>}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs ml-2"
                  onClick={() => navigate('/admin/whatsapp/settings')}
                  data-testid="go-to-settings-btn"
                >
                  Open Settings →
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {config && config.configured && (
          <Alert variant="default" className="border-green-400 bg-green-50 dark:bg-green-950/20" data-testid="config-ok">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <AlertDescription className="text-xs text-green-800 dark:text-green-200">
              Integration active — API version {config.api_version}
            </AlertDescription>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedThread(null); }}>
          <TabsList className="flex overflow-x-auto">
            <TabsTrigger value="active" data-testid="tab-active">
              Active {totalUnread > 0 && <Badge variant="destructive" className="ml-2">{totalUnread}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="archived" data-testid="tab-archived">Archived</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">
              Leads {leads.filter(l => l.status === 'new').length > 0 && <Badge className="ml-2">{leads.filter(l => l.status === 'new').length}</Badge>}
            </TabsTrigger>
          </TabsList>

          {/* Active / Archived */}
          {(activeTab === 'active' || activeTab === 'archived') && (
            <TabsContent value={activeTab} className="mt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 min-h-[60vh]">
                {/* Thread list */}
                <Card className={`lg:col-span-1 ${selectedThread ? 'hidden lg:block' : ''}`}>
                  <CardHeader className="p-3 lg:p-4">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2 top-3 text-muted-foreground" />
                      <Input
                        placeholder="Search threads..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-8"
                        data-testid="thread-search-input"
                      />
                    </div>
                  </CardHeader>
                  <CardContent className="p-0 max-h-[70vh] overflow-y-auto">
                    {loading && (
                      <div className="p-6 text-center text-sm text-muted-foreground">
                        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                        Loading threads...
                      </div>
                    )}
                    {!loading && filteredThreads.length === 0 && (
                      <div className="p-6 text-center text-sm text-muted-foreground" data-testid="empty-threads">
                        <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                        No {activeTab} conversations yet.
                      </div>
                    )}
                    {!loading && filteredThreads.map((t) => (
                      <button
                        key={t.id}
                        onClick={() => setSelectedThread(t)}
                        className={`w-full text-left p-3 border-b hover:bg-accent transition-colors ${
                          selectedThread?.id === t.id ? 'bg-accent' : ''
                        }`}
                        data-testid={`thread-row-${t.id}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold truncate text-sm">
                                {t.contact_name || t.parent_name || t.display_phone || `+${t.phone}`}
                              </span>
                              {t.thread_type === 'parent' && (
                                <Badge variant="outline" className="text-[10px] py-0 h-4">Parent</Badge>
                              )}
                              {t.thread_type === 'lead' && (
                                <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-400 text-amber-700">Lead</Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              {t.display_phone || `+${t.phone}`}
                            </div>
                            <div className="text-xs text-muted-foreground truncate mt-1">
                              {t.last_message_direction === 'outbound' && <span className="text-blue-600">→ </span>}
                              {t.last_message_body || 'No messages yet'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            <span className="text-[10px] text-muted-foreground">{relativeTime(t.last_message_at)}</span>
                            {(t.unread_count || 0) > 0 && (
                              <Badge variant="destructive" className="text-[10px] h-4 px-1.5">{t.unread_count}</Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </CardContent>
                </Card>

                {/* Conversation */}
                <Card className={`lg:col-span-2 ${!selectedThread ? 'hidden lg:flex lg:items-center lg:justify-center' : 'flex flex-col'}`}>
                  {!selectedThread && (
                    <div className="p-10 text-center text-sm text-muted-foreground">
                      <Info className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      Select a conversation to view messages.
                    </div>
                  )}
                  {selectedThread && (
                    <>
                      <CardHeader className="p-3 lg:p-4 border-b">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="lg:hidden h-7 px-2 mb-1"
                              onClick={() => setSelectedThread(null)}
                              data-testid="back-to-threads-btn"
                            >
                              ← Back
                            </Button>
                            <CardTitle className="text-base flex items-center gap-2">
                              {selectedThread.thread_type === 'parent' ? (
                                <User className="w-4 h-4 text-blue-600" />
                              ) : (
                                <UserPlus className="w-4 h-4 text-amber-600" />
                              )}
                              <span className="truncate">
                                {selectedThread.contact_name || selectedThread.parent_name || selectedThread.display_phone}
                              </span>
                            </CardTitle>
                            <CardDescription className="text-xs flex items-center gap-1 mt-0.5">
                              <PhoneCall className="w-3 h-3" />
                              {selectedThread.display_phone || `+${selectedThread.phone}`}
                              {selectedThread.parent_id && <span className="ml-2 text-green-600">Linked to parent</span>}
                            </CardDescription>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleArchive(selectedThread)}
                            data-testid="toggle-archive-btn"
                          >
                            {selectedThread.status === 'archived' ? (
                              <><ArchiveRestore className="w-3.5 h-3.5 mr-1" />Unarchive</>
                            ) : (
                              <><Archive className="w-3.5 h-3.5 mr-1" />Archive</>
                            )}
                          </Button>
                        </div>
                      </CardHeader>

                      <CardContent className="p-0 flex flex-col flex-1">
                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-muted/30 min-h-[300px] max-h-[50vh]">
                          {loadingMessages && (
                            <div className="text-center text-sm text-muted-foreground py-8">
                              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                            </div>
                          )}
                          {!loadingMessages && messages.length === 0 && (
                            <div className="text-center text-sm text-muted-foreground py-8">
                              No messages yet.
                            </div>
                          )}
                          {messages.map((m) => (
                            <div
                              key={m.id}
                              className={`flex ${m.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                              data-testid={`message-${m.id}`}
                            >
                              <div
                                className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                  m.direction === 'outbound'
                                    ? 'bg-green-600 text-white rounded-br-sm'
                                    : 'bg-background rounded-bl-sm'
                                }`}
                              >
                                <div className="whitespace-pre-wrap break-words">{m.body || <em className="opacity-70">[{m.message_type}]</em>}</div>
                                <div className={`text-[10px] mt-1 flex items-center gap-1 ${
                                  m.direction === 'outbound' ? 'text-green-100' : 'text-muted-foreground'
                                }`}>
                                  <span>{relativeTime(m.created_at)}</span>
                                  {m.direction === 'outbound' && m.status && (
                                    <span className="opacity-80">· {m.status}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>

                        {/* Composer */}
                        <div className="p-3 border-t bg-background">
                          <div className="flex items-end gap-2">
                            <Textarea
                              placeholder="Type a reply (valid within 24h of last inbound message)..."
                              value={reply}
                              onChange={(e) => setReply(e.target.value)}
                              rows={2}
                              className="resize-none"
                              disabled={!config?.configured || sending}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                  e.preventDefault();
                                  sendReply();
                                }
                              }}
                              data-testid="reply-textarea"
                            />
                            <div className="flex flex-col gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={suggestReply}
                                disabled={suggesting || sending}
                                title="Draft a reply with AI. Tip: type a short note first to steer the AI."
                                data-testid="suggest-reply-btn"
                              >
                                {suggesting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <>
                                    <Sparkles className="w-4 h-4 mr-1" />
                                    <span className="hidden sm:inline">Suggest</span>
                                  </>
                                )}
                              </Button>
                              <Button
                                onClick={sendReply}
                                disabled={!reply.trim() || !config?.configured || sending}
                                data-testid="send-reply-btn"
                              >
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                          {!config?.configured && (
                            <div className="text-[11px] text-amber-700 mt-1">
                              Sending disabled — add WhatsApp credentials in backend/.env.
                            </div>
                          )}
                          <div className="text-[11px] text-muted-foreground mt-1">Tip: ⌘/Ctrl + Enter to send. Click Suggest for an AI draft — type a note first to steer it.</div>
                        </div>
                      </CardContent>
                    </>
                  )}
                </Card>
              </div>
            </TabsContent>
          )}

          {/* Leads */}
          {activeTab === 'leads' && (
            <TabsContent value="leads" className="mt-4">
              <Card>
                <CardHeader className="p-4">
                  <CardTitle className="text-base">Lead Capture</CardTitle>
                  <CardDescription className="text-xs">
                    Unknown phone numbers that messaged the business. Follow up to convert.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {loading && (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
                    </div>
                  )}
                  {!loading && leads.length === 0 && (
                    <div className="p-6 text-center text-sm text-muted-foreground" data-testid="empty-leads">
                      No leads captured yet.
                    </div>
                  )}
                  {!loading && leads.map((lead) => (
                    <div key={lead.id} className="p-3 border-b hover:bg-accent/50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-sm">
                              {lead.display_name || lead.display_phone}
                            </span>
                            <Badge
                              variant={lead.status === 'new' ? 'default' : 'outline'}
                              className="text-[10px] py-0 h-4"
                            >
                              {lead.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-muted-foreground mt-0.5">
                            {lead.display_phone} · {lead.message_count} msg · {relativeTime(lead.last_message_at)}
                          </div>
                          <div className="text-xs italic mt-1 max-w-2xl">"{lead.first_message}"</div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateLead(lead, 'contacted')} data-testid={`mark-contacted-${lead.id}`}>
                            Contacted
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateLead(lead, 'qualified')} data-testid={`mark-qualified-${lead.id}`}>
                            Qualified
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateLead(lead, 'converted')} data-testid={`mark-converted-${lead.id}`}>
                            Converted
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => updateLead(lead, 'dismissed')} data-testid={`mark-dismissed-${lead.id}`}>
                            Dismiss
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
